import type React from 'react';
import type { MouseEvent } from 'react';
import { lazy, Suspense, useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTypedSelector, useTypedDispatch } from '../../Store/connectors';
import { addCartItem } from '../../Store/CartItem/action';
import FormatPrice from '../../../general/Format/FormatPrice';
import Translation from '../../../general/Translation/Translation';
import AmountSelector from '../AmountSelector/AmountSelector';
import Backdrop from '../../../../stories/Atoms/Backdrop/Backdrop';
import DeleteModal from '../Modal/DeleteModal';
import type { UpsellProduct } from '../../Store/Upsell/state';
import styles from './Upsell.module.scss';
import SideBar from '../../../general/SideBar/SideBar';
import Picture from '../../../general/Picture/Picture';
import { removeUpsell, setUpsellAmount } from '../../Store/Upsell/action';
import SideBarHeader from '../../../general/SideBar/SideBarContent/SideBarHeader';
import SideBarBody from '../../../general/SideBar/SideBarContent/SideBarBody';
import Button, { Size } from '../../../general/Button/Button';
import Source from '../../../general/Picture/Source';
import { TopLayerContext } from '../../../general/TopLayer/TopLayerContext';
import { useWithImageUrl } from '../../../general/WebshopContext/WebshopContext';
import Icon from '../../../../stories/Atoms/Icon/Icon';

export interface UpsellProps {
  hasSiblings: boolean;
  parentCartItemId: string;
  parentProductId: number;
  upsellGroupId: number;
  upsellProduct: UpsellProduct;
}

const Upsell: React.FC<UpsellProps> = ({
  hasSiblings,
  parentCartItemId,
  parentProductId,
  upsellGroupId,
  upsellProduct,
}) => {
  const {
    amountInCart,
    amountMutable,
    cartItemId,
    editHash,
    extraInformation,
    id,
    inCart,
    locked,
    name,
    personalized,
    price,
    uniqueId,
  } = upsellProduct;
  const { closeTopLayerElement, createTopLayerElement } =
    useContext(TopLayerContext);
  const imgUrl = upsellProduct.previewImage ?? upsellProduct.imgUrl;
  const fastlyImageUrl = `${imgUrl}?width=128&crop=1:1&bg-color=ffffff&format=jpg`;
  const withImageUrl = useWithImageUrl();

  const priceValue = amountInCart
    ? price.numericValue * amountInCart
    : price.numericValue;

  const [showDelete, setShowDelete] = useState<boolean>(false);
  const dispatch = useTypedDispatch();

  const currency = useTypedSelector(({ metaData }) => metaData.currency);

  const addUpsellToCart = useCallback(
    (num = 1): void => {
      dispatch(
        addCartItem({
          amount: num,
          parentCartItemId,
          parentProductId,
          productId: id,
          upsellGroupId,
        }),
      );
    },
    [dispatch, id, parentCartItemId, parentProductId, upsellGroupId],
  );

  const handleShowUpsellEditor = useCallback((): void => {
    const UpsellEditorApiProvider = lazy(
      () => import('./Editor/UpsellEditorApiProvider'),
    );
    const PersonalisedUpsellEditor = lazy(
      () => import('./Editor/PersonalisedUpsellEditor'),
    );
    const isCartEdit = Boolean(editHash && uniqueId);

    const loader = (
      <div className={styles.EditorLoadFeedback}>
        <img
          alt="Loading..."
          src={withImageUrl('/editor/loader/default.gif')}
        />
      </div>
    );

    const loadFailed = (
      <div className={styles.EditorLoadFeedback}>
        <img
          alt="Load failed"
          src={withImageUrl('/editor/broken-pencil.png')}
        />
      </div>
    );

    createTopLayerElement(
      <SideBar key={`editor-${id}-${parentCartItemId}`}>
        <SideBarHeader headerText={name} />
        <SideBarBody>
          <Suspense fallback={loader}>
            <UpsellEditorApiProvider
              loader={loader}
              loadFailed={loadFailed}
              cart={{ editHash, isCartEdit, uniqueId }}
              product={{ productId: id, variantId: 0 }}
            >
              <PersonalisedUpsellEditor
                onAddedToCart={closeTopLayerElement}
                isCartEdit={isCartEdit}
                upsellGroupId={upsellGroupId}
                parentCartItemId={parentCartItemId}
                parentProductId={parentProductId}
                productId={id}
              />
            </UpsellEditorApiProvider>
          </Suspense>
        </SideBarBody>
      </SideBar>,
      SideBar.ANIMATION_TIME,
    );
  }, [
    name,
    createTopLayerElement,
    closeTopLayerElement,
    upsellGroupId,
    parentCartItemId,
    parentProductId,
    editHash,
    uniqueId,
    id,
    withImageUrl,
  ]);

  const handleUpsellAddToCart = useCallback(
    (isPersonalized: boolean, closeBar: boolean) => {
      if (isPersonalized) {
        handleShowUpsellEditor();
      } else {
        addUpsellToCart();
        if (closeBar) {
          closeTopLayerElement();
        }
      }
    },
    [addUpsellToCart, handleShowUpsellEditor, closeTopLayerElement],
  );

  const showUpsellInfo = useCallback(
    () =>
      createTopLayerElement(
        <SideBar key={`info-${id}`}>
          <SideBarHeader headerText={name} />
          <SideBarBody>
            <div className={styles.UpsellInfo}>
              <Picture
                imageUrl={imgUrl}
                alt={name}
              >
                <Source
                  imageWidth={358}
                  minScreenWidth={768}
                />
                <Source
                  imageWidth={576}
                  minScreenWidth={576}
                />
                <Source imageWidth={300} />
              </Picture>
              <div className={styles.UpsellInfo__text}>{extraInformation}</div>

              <div className={styles.Upsell__sidebarButton}>
                {!inCart && (
                  <Button
                    isGhost
                    size={Size.xs}
                    onClick={() => {
                      handleUpsellAddToCart(personalized, true);
                    }}
                  >
                    <span>
                      {personalized ? (
                        <Translation
                          pageString="upsell"
                          stringId="personalize"
                        />
                      ) : (
                        <Translation
                          pageString="upsell"
                          stringId="add_button_text"
                        />
                      )}
                    </span>
                  </Button>
                )}
              </div>
            </div>
          </SideBarBody>
        </SideBar>,
        SideBar.ANIMATION_TIME,
      ),
    [
      createTopLayerElement,
      extraInformation,
      handleUpsellAddToCart,
      id,
      imgUrl,
      inCart,
      name,
      personalized,
    ],
  );

  const showDeleteModal = (e: MouseEvent) => {
    if (!cartItemId || cartItemId === '0') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setShowDelete(true);
  };

  const setAmount = (amount: string) => {
    if (!cartItemId || cartItemId === '0') {
      return;
    }
    dispatch(
      setUpsellAmount({
        amount: Number(amount),
        id: cartItemId,
        parentCartItemId: String(parentCartItemId),
      }),
    );
  };

  const closeDeleteModal = () => {
    setShowDelete(false);
  };

  const removeUpsellFromCart = (): void => {
    if (!cartItemId || cartItemId === '0') {
      return;
    }
    closeDeleteModal();
    dispatch(
      removeUpsell({
        id: cartItemId,
        parentCartItemId,
        parentProductId,
        productId: id,
      }),
    );
  };

  const removeUpsellModal = showDelete && (
    <Backdrop onClose={closeDeleteModal}>
      <DeleteModal
        onAccept={removeUpsellFromCart}
        onClose={closeDeleteModal}
      />
    </Backdrop>
  );

  return (
    <>
      {removeUpsellModal}
      <div
        className={classNames(styles.Upsell__wrapper, {
          [styles.hasSiblings as string]: hasSiblings,
        })}
      >
        {inCart && (
          <div className={styles.Upsell__added}>
            <Icon
              style="fa-solid"
              name="fa-circle-check"
              size="fa-xl"
              className="mr-2 c-secondary-02"
            />
          </div>
        )}
        <div
          className={classNames(
            styles.Upsell__container,
            styles.Upsell__container__top,
          )}
        >
          <img
            className={classNames(styles.Upsell__image, 'pointer')}
            draggable={false}
            loading="lazy"
            alt={name}
            src={fastlyImageUrl}
            height="64"
            width="64"
            onClick={() => showUpsellInfo()}
            data-clarity-mask="True"
          />
          <div className={classNames(styles.Upsell__title, 'pointer')}>
            <span
              className={styles.Upsell__title__main}
              onClick={() => showUpsellInfo()}
            >
              {name}
            </span>
            {personalized && inCart && (
              <span
                onClick={() => handleShowUpsellEditor()}
                className={classNames(styles.Upsell__title__sub, 'pointer')}
              >
                <Translation
                  pageString="winkelmand_teksten"
                  stringId="change_personalisation"
                />
              </span>
            )}
          </div>
        </div>
        <div
          className={classNames(
            styles.Upsell__container,
            styles.Upsell__container__bottom,
          )}
        >
          <div>
            {inCart && !locked && (
              <Button
                isGhost
                isClean
                icon="fa-trash-can"
                iconOnly
                onClick={showDeleteModal}
                data-testid="remove-upsell"
                size={Size.xs}
                aria-label="Remove upsell"
              />
            )}
            {!inCart && (
              <Button
                isGhost
                size={Size.xs}
                onClick={() => {
                  handleUpsellAddToCart(personalized, false);
                }}
              >
                <span>
                  {personalized ? (
                    <Translation
                      pageString="upsell"
                      stringId="personalize"
                    />
                  ) : (
                    <Translation
                      pageString="upsell"
                      stringId="add_button_text"
                    />
                  )}
                </span>
              </Button>
            )}
          </div>
          {amountMutable && inCart && (
            <AmountSelector
              product={{
                amount: amountInCart,
                displayAmount: String(amountInCart),
              }}
              onAmountChange={(amount) => setAmount(amount)}
            />
          )}
          {price.numericValue !== 0 && (
            <div
              className={styles.Upsell__title__sub__price}
              data-testid="price"
            >
              <FormatPrice price={{ currency, value: priceValue }} />
            </div>
          )}
          {price.numericValue === 0 && (
            <div
              className={styles.Upsell__title__sub__price}
              data-testid="price"
            >
              <Translation
                pageString="discount"
                stringId="free_product_discount_value"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Upsell;
